// Browser side of Google and Telegram sign-in. Each helper yields a credential for the
// backend to verify; a page never trusts the decoded profile itself.
import { telegramNonce } from '../../helpers/authApi';

interface GoogleCodeResponse { code?: string; state?: string; error?: string }
interface GoogleWindow {
  google?: { accounts?: { oauth2?: { initCodeClient(options: {
    client_id: string; scope: string; ux_mode: 'popup'; state: string;
    callback(response: GoogleCodeResponse): void;
    error_callback?(error: { type?: string }): void;
  }): { requestCode(): void } } } };
  Telegram?: { Login?: { auth(
    options: { client_id: string; nonce: string; scope?: string },
    callback: (result: { id_token?: string; error?: string }) => void,
  ): void } };
}
const page = () => window as unknown as GoogleWindow;

const scripts = new Map<string, Promise<void>>();
function loadScript(src: string) {
  let loading = scripts.get(src);
  if (!loading) {
    loading = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => { scripts.delete(src); reject(new Error(`Could not load ${src}`)); };
      document.head.appendChild(script);
    });
    scripts.set(src, loading);
  }
  return loading;
}

export const prepareGoogle = async () => {
  if (!page().google?.accounts?.oauth2) await loadScript('https://accounts.google.com/gsi/client');
};

// Must run inside the click handler, so the script must already be prepared or popups get blocked.
// Resolves null when the person closes the popup.
export function requestGoogleCode(clientId: string): Promise<string | null> {
  const oauth = page().google?.accounts?.oauth2;
  if (!oauth) return Promise.reject(new Error('Google sign-in is still loading.'));
  const state = crypto.randomUUID();
  return new Promise((resolve, reject) => {
    oauth.initCodeClient({
      client_id: clientId, scope: 'openid email', ux_mode: 'popup', state,
      callback(response) {
        if (response.error === 'access_denied') resolve(null);
        else if (!response.code || response.state !== state) reject(new Error('Google sign-in failed.'));
        else resolve(response.code);
      },
      error_callback(error) {
        if (error.type === 'popup_closed') resolve(null);
        else reject(new Error('Google sign-in failed.'));
      },
    }).requestCode();
  });
}

// Holds one server-issued nonce so that open() can run synchronously in the click handler.
export function createTelegramLogin(clientId: string) {
  let nonce = '';
  return {
    async prepare() {
      if (!page().Telegram?.Login) await loadScript('https://oauth.telegram.org/js/telegram-login.js');
      nonce = (await telegramNonce()).nonce;
    },
    // Resolves null when the person closes the popup. Signing up also asks to share the phone number.
    open(askForPhone = false): Promise<string | null> {
      const login = page().Telegram?.Login;
      if (!login || !nonce) return Promise.reject(new Error('Telegram sign-in is still loading.'));
      const used = nonce;
      nonce = '';
      return new Promise((resolve) => {
        login.auth({ client_id: clientId, nonce: used, ...(askForPhone ? { scope: 'profile phone' } : {}) }, (result) => resolve(result.id_token ?? null));
      });
    },
  };
}
